import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, ShieldCheck, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { resizeImage } from "@/lib/image";

const STATUS_META = {
  approved: { label: "Ověřeno", color: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  pending: { label: "Čeká na ověření", color: "bg-amber-100 text-amber-800", Icon: AlertCircle },
  rejected: { label: "Zamítnuto", color: "bg-rose-100 text-rose-800", Icon: AlertCircle },
  not_submitted: { label: "Neodesláno", color: "bg-slate-100 text-slate-700", Icon: AlertCircle },
};

const ProviderVerification = () => {
  const [status, setStatus] = useState("loading");
  const [idPhoto, setIdPhoto] = useState("");
  const [selfie, setSelfie] = useState("");
  const [iban, setIban] = useState("");
  const [fullLegalName, setFullLegalName] = useState("");
  const [reason, setReason] = useState("");
  const [submitted, setSubmitted] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/providers/me/verification");
      setStatus(data.status || "not_submitted");
      setIban(data.iban || "");
      setFullLegalName(data.full_legal_name || "");
      setReason(data.rejection_reason || "");
      setSubmitted(data.submitted_at || null);
    } catch (e) {
      setStatus("not_submitted");
    }
  };
  useEffect(() => { load(); }, []);

  const handleFile = (setter) => async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      setter(await resizeImage(f));
    } catch (err) {
      toast.error(err.message || "Soubor nelze nahrát");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!idPhoto || !selfie || !iban) {
      toast.error("Doplňte foto OP, selfie a číslo účtu");
      return;
    }
    const cleaned = iban.replace(/\s/g, "");
    const okCzech = /^(\d{1,6}-)?\d{2,10}\/\d{4}$/.test(cleaned);
    const okIban = /^CZ\d{22}$/i.test(cleaned);
    if (!okCzech && !okIban) {
      toast.error("Neplatné číslo účtu. Použijte český formát, např. 123456789/0100");
      return;
    }
    setSaving(true);
    try {
      await api.put("/providers/me/verification", {
        id_photo: idPhoto, selfie, iban: cleaned, full_legal_name: fullLegalName,
      });
      toast.success("Žádost odeslána – ověření do 24 h");
      await load();
      setIdPhoto(""); setSelfie("");
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSaving(false); }
  };

  if (status === "loading") return (<div><Navbar /><div className="container-page py-16 text-center">Načítání…</div></div>);

  const meta = STATUS_META[status] || STATUS_META.not_submitted;
  const StatusIcon = meta.Icon;
  const canSubmit = ["not_submitted", "rejected"].includes(status);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container-page py-10 max-w-3xl">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="w-6 h-6 text-sky-600" />
          <div className="tiny-label">Pro prádelny</div>
        </div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Verifikace prádelny</h1>
        <p className="text-slate-600 mt-2">Pro vyšší důvěru zákazníků a vyplácení odměn potvrďte svoji totožnost a bankovní účet.</p>

        <Card className={`mt-6 p-5 flex items-center gap-3 ${meta.color}`}>
          <StatusIcon className="w-5 h-5" />
          <div className="flex-1">
            <div className="font-semibold" data-testid="verification-status">Stav: {meta.label}</div>
            {submitted && <div className="text-xs">Odesláno {new Date(submitted).toLocaleString("cs-CZ")}</div>}
            {status === "rejected" && reason && (
              <div className="text-xs mt-1">Důvod zamítnutí: {reason}</div>
            )}
          </div>
        </Card>

        {canSubmit && (
          <Card className="mt-6 p-7">
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label>Celé jméno (jak je na OP)</Label>
                <Input value={fullLegalName} onChange={(e) => setFullLegalName(e.target.value)} placeholder="Jméno Příjmení" data-testid="verification-legal-name" />
              </div>
              <div>
                <Label>Číslo bankovního účtu (CZ formát)</Label>
                <Input
                  value={iban}
                  onChange={(e) => setIban(e.target.value)}
                  placeholder="123456789/0100"
                  data-testid="verification-iban"
                  required
                  inputMode="text"
                  pattern="^(\d{1,6}-)?\d{2,10}/\d{4}$|^CZ\d{22}$"
                  title="Formát: číslo/kód banky (např. 123456789/0100). Akceptujeme i IBAN CZ…"
                />
                <p className="text-xs text-slate-500 mt-1">Zadejte ve tvaru <code className="px-1 rounded bg-slate-100">číslo/kód-banky</code> (např. <strong>123456789/0100</strong>) nebo IBAN.</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <FileBlock label="Foto občanského průkazu (přední strana)" value={idPhoto} onChange={handleFile(setIdPhoto)} testid="verification-id-photo" />
                <FileBlock label="Selfie s OP v ruce" value={selfie} onChange={handleFile(setSelfie)} testid="verification-selfie" />
              </div>

              <div className="text-xs text-slate-500">
                Vaše dokumenty zpracujeme dle GDPR a uložíme zašifrované. Nezasíláme je třetím stranám.
              </div>

              <Button type="submit" disabled={saving} data-testid="verification-submit">
                {saving ? "Odesílám…" : "Odeslat k ověření"}
              </Button>
            </form>
          </Card>
        )}

        {status === "pending" && (
          <Card className="mt-6 p-7 text-slate-600">
            <p>Vaše žádost je v ověřování. Obvykle do 24 hodin.</p>
            <p className="mt-2 text-sm">Po schválení získáte odznak <Badge variant="secondary">Ověřeno ✓</Badge> a vyšší pořadí ve výsledcích.</p>
          </Card>
        )}

        {status === "approved" && (
          <Card className="mt-6 p-7 bg-emerald-50 border-emerald-200">
            <p className="text-emerald-900 font-semibold">Vaše prádelna je ověřená. Skvělá práce!</p>
          </Card>
        )}
      </main>
      <Footer />
    </div>
  );
};

const FileBlock = ({ label, value, onChange, testid }) => (
  <div>
    <Label>{label}</Label>
    <label className="mt-2 block cursor-pointer">
      <div className="border-2 border-dashed border-slate-300 rounded-xl p-4 hover:border-sky-400 transition-all">
        {value ? (
          <img src={value} alt="náhled" className="w-full h-40 object-cover rounded-lg" />
        ) : (
          <div className="flex flex-col items-center gap-2 py-8 text-slate-500 text-sm">
            <Upload className="w-6 h-6" />
            <span>Klikněte pro nahrání</span>
          </div>
        )}
      </div>
      <input type="file" accept="image/*" className="hidden" onChange={onChange} data-testid={testid} />
    </label>
  </div>
);

export default ProviderVerification;
